import { useState, useEffect, useCallback, useRef } from "react";
import { Link as ScrollLink } from "react-scroll";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CONTENT } from "../constants/content";
import logoUrl from "../assets/images/logo.png";
import logo96 from "../assets/images/logo-96.webp";
import logo192 from "../assets/images/logo-192.webp";
import styles from "../styles/Navbar.module.scss";

// The mark renders at ~38px; 42KB of PNG was 10x more than it needs.
const LOGO_SRCSET = `${logo96} 96w, ${logo192} 192w`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const { logo, links, cta } = CONTENT.navbar;
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  // Condense on scroll and drive a reading-progress hairline. The bar stays
  // visible at all times. One rAF-throttled listener.
  useEffect(() => {
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      const max =
        document.documentElement.scrollHeight - window.innerHeight;

      setIsScrolled(y > 24);
      setProgress(max > 0 ? Math.min(1, Math.max(0, y / max)) : 0);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const closeMenu = useCallback(() => setIsOpen(false), []);

  // Lock the page behind the mobile overlay and allow Escape to dismiss it.
  useEffect(() => {
    if (!isOpen) return undefined;

    /*
      body.is-locked is `position: fixed` because iOS Safari scrolls straight
      through `overflow: hidden`. Fixing the body collapses the page to scroll
      0, so the offset is parked in `top` while the menu is open and handed
      back verbatim on close — otherwise every menu dismissal would dump the
      visitor at the top of the page.
    */
    const scrollY = window.scrollY;
    document.body.style.top = `-${scrollY}px`;
    document.body.classList.add("is-locked");
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    const firstLink = panelRef.current?.querySelector(
      "a, button, [tabindex]:not([tabindex='-1'])"
    );
    firstLink?.focus();

    return () => {
      document.body.classList.remove("is-locked");
      document.body.style.top = "";
      // `scroll-behavior: smooth` on :root would animate this restore and let
      // the visitor watch the page fly back; "instant" pins it in one frame.
      window.scrollTo({ top: scrollY, behavior: "instant" });
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const handleNavigateToSection = (sectionName) => {
    // From non-home pages, navigate to "/" and tell home which section to scroll to
    navigate("/", { state: { scrollTo: sectionName } });
    setIsOpen(false);
  };

  const renderLink = (link) =>
    isHome ? (
      <ScrollLink
        key={link.name}
        to={link.to}
        smooth={true}
        duration={600}
        offset={-72}
        spy={true}
        hashSpy={false}
        activeClass={styles.isActive}
        className={styles.navLink}
        tabIndex={0}
        onClick={closeMenu}
      >
        {link.name}
      </ScrollLink>
    ) : (
      <button
        key={link.name}
        type="button"
        className={styles.navLink}
        onClick={() => handleNavigateToSection(link.to)}
      >
        {link.name}
      </button>
    );

  return (
    <header
      className={[
        styles.navbar,
        isScrolled ? styles.scrolled : "",
        isOpen ? styles.menuOpen : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span
        className={styles.progress}
        style={{ "--progress": progress }}
        aria-hidden="true"
      />

      <div className={styles.inner}>
        <div className={styles.brand}>
          {isHome ? (
            <ScrollLink
              to="hero"
              smooth={true}
              duration={600}
              className={styles.brandLink}
              tabIndex={0}
              onClick={closeMenu}
            >
              <img
                src={logoUrl}
                srcSet={LOGO_SRCSET}
                sizes="40px"
                alt=""
                aria-hidden="true"
                width="40"
                height="40"
              />
              <span>{logo}</span>
            </ScrollLink>
          ) : (
            <Link to="/" className={styles.brandLink} onClick={closeMenu}>
              <img
                src={logoUrl}
                srcSet={LOGO_SRCSET}
                sizes="40px"
                alt=""
                aria-hidden="true"
                width="40"
                height="40"
              />
              <span>{logo}</span>
            </Link>
          )}
        </div>

        <nav className={styles.desktopNav} aria-label="Navigazione principale">
          {links.map(renderLink)}
        </nav>

        <div className={styles.actions}>
          {isHome ? (
            <ScrollLink
              to="register"
              smooth={true}
              duration={600}
              offset={-72}
              className={styles.ctaButton}
              tabIndex={0}
              onClick={closeMenu}
            >
              {cta}
            </ScrollLink>
          ) : (
            <button
              type="button"
              className={styles.ctaButton}
              onClick={() => handleNavigateToSection("register")}
            >
              {cta}
            </button>
          )}

          <button
            ref={toggleRef}
            type="button"
            className={styles.hamburger}
            onClick={() => setIsOpen((open) => !open)}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? "Chiudi il menu" : "Apri il menu"}
          >
            <span />
            <span />
          </button>
        </div>
      </div>

      <div
        id="mobile-menu"
        ref={panelRef}
        className={styles.mobilePanel}
        hidden={!isOpen}
      >
        <nav className={styles.mobileNav} aria-label="Navigazione mobile">
          {links.map((link, index) => (
            <span
              key={link.name}
              className={styles.mobileItem}
              style={{ "--i": index }}
            >
              {renderLink(link)}
            </span>
          ))}
        </nav>

        {isHome ? (
          <ScrollLink
            to="register"
            smooth={true}
            duration={600}
            offset={-72}
            className={styles.mobileCta}
            tabIndex={0}
            onClick={closeMenu}
          >
            {cta}
          </ScrollLink>
        ) : (
          <button
            type="button"
            className={styles.mobileCta}
            onClick={() => handleNavigateToSection("register")}
          >
            {cta}
          </button>
        )}
      </div>
    </header>
  );
};

export default Navbar;
